import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Extension } from "@tiptap/core";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import "@tiptap/extension-text-style";
import { ImageDown, Link2 } from "lucide-react";
import { Video } from "iconsax-react";

// ۱. اکستنشن سفارشی اندازه فونت
export const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

// ۲. اکستنشن سفارشی ویدیو
const VideoExtension = Extension.create({
  name: "video",
  group: "block",
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
      width: { default: "100%" },
    };
  },
  parseHTML() {
    return [{ tag: "video[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      { class: "video-wrapper my-4 flex justify-center" },
      [
        "video",
        { ...HTMLAttributes, class: "rounded-lg max-w-full shadow-sm" },
      ],
    ];
  },
  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs: options });
        },
    };
  },
});

// ۳. توسعه اکستنشن عکس برای پشتیبانی از alt, width, height
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      alt: { default: null },
      width: { default: null },
      height: { default: null },
    };
  },
});

const CustomEditor = ({
  value,
  onEditorChange,
  placeholder,
  directionality = "rtl",
}) => {
  // استیت‌های مدال‌ها و ویورسورس
  const [modalType, setModalType] = useState(null); // 'image' | 'link' | 'video' | null
  const [modalData, setModalData] = useState({
    src: "",
    alt: "",
    width: "",
    height: "",
  });
  const [viewSource, setViewSource] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      FontSize,
      VideoExtension,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-blue-600 underline cursor-pointer" },
      }),
      CustomImage.configure({
        HTMLAttributes: {
          class: "rounded-lg max-w-full my-4 mx-auto block shadow-sm",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "border-collapse table-auto w-full my-4",
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class:
            "border border-neutral-300 bg-neutral-100 p-2 font-bold text-right",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-neutral-300 p-2 text-right",
        },
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        // اضافه کردن استایل‌های مستقیم برای نمایش درست جدول در محیط ادیتور
        class: `prose max-w-none focus:outline-none min-h-[250px] p-4 text-neutral-800 bg-white [&_table]:border-collapse [&_table]:w-full [&_td]:border [&_td]:border-neutral-300 [&_td]:p-2 [&_th]:border [&_th]:border-neutral-300 [&_th]:p-2 [&_th]:bg-neutral-50`,
        style: `direction: ${directionality}`,
      },
    },
    onUpdate: ({ editor }) => {
      const currentHTML = editor.getHTML();
      setHtmlContent(currentHTML);
      if (onEditorChange) {
        onEditorChange(currentHTML);
      }
    },
  });

  // همگام‌سازی دیتای ورودی از خارج ادیتور
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
      setHtmlContent(value);
    }
  }, [value, editor]);

  if (!editor) return null;

  // مدیریت باز شدن مدال‌ها
  const openModal = (type) => {
    if (type === "link" && editor.isActive("link")) {
      setModalData({
        src: editor.getAttributes("link").href || "",
        alt: "",
        width: "",
        height: "",
      });
    } else {
      setModalData({ src: "", alt: "", width: "", height: "" });
    }
    setModalType(type);
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    if (!modalData.src) return;

    if (modalType === "image") {
      editor
        .chain()
        .focus()
        .setImage({
          src: modalData.src,
          alt: modalData.alt || undefined,
          width: modalData.width || undefined,
          height: modalData.height || undefined,
        })
        .run();
    } else if (modalType === "video") {
      editor.chain().focus().setVideo({ src: modalData.src }).run();
    } else if (modalType === "link") {
      editor.chain().focus().setLink({ href: modalData.src }).run();
    }
    setModalType(null);
  };

  // هماهنگی تغییرات مستقیم در تگ‌های HTML در حالت View Source
  const handleSourceChange = (e) => {
    const newVal = e.target.value;
    setHtmlContent(newVal);
    if (onEditorChange) {
      onEditorChange(newVal);
    }
  };

  // سوییچ بین حالت گرافیکی و کد خام HTML
  const toggleSourceView = () => {
    if (viewSource) {
      editor.commands.setContent(htmlContent);
    }
    setViewSource(!viewSource);
  };

  const MenuButton = ({ onClick, isActive, children, title, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-2 rounded-md transition-all duration-200 text-sm font-medium ${
        disabled ? "opacity-40 cursor-not-allowed" : ""
      } ${
        isActive
          ? "bg-neutral-900 text-white shadow-sm"
          : "text-neutral-600 hover:bg-neutral-200/70"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div
      className="w-full border border-neutral-300 rounded-xl overflow-hidden bg-white shadow-sm transition-all focus-within:ring-2 focus-within:ring-neutral-900/5"
      style={{ direction: directionality }}
    >
      {/* تولبار کاملاً لایت */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-neutral-100 border-b border-neutral-300 select-none">
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
          disabled={viewSource}
        >
          ↩
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
          disabled={viewSource}
        >
          ↪
        </MenuButton>

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        {/* انتخاب اندازه فونت */}
        <select
          disabled={viewSource}
          onChange={(e) => {
            if (e.target.value === "normal")
              editor.chain().focus().unsetFontSize().run();
            else editor.chain().focus().setFontSize(e.target.value).run();
          }}
          className="bg-white border border-neutral-300 text-neutral-700 text-xs rounded px-2 py-1.5 outline-none cursor-pointer disabled:opacity-40"
        >
          <option value="normal">اندازه فونت</option>
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="24px">24px</option>
          <option value="32px">32px</option>
        </select>

        {/* انتخاب رنگ متن */}
        <input
          type="color"
          disabled={viewSource}
          onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
          className="w-7 h-7 p-0.5 border border-neutral-300 bg-white rounded cursor-pointer disabled:opacity-40"
          title="رنگ متن"
        />

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          isActive={editor.isActive("heading", { level: 1 })}
          title="H1"
          disabled={viewSource}
        >
          H1
        </MenuButton>
        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          isActive={editor.isActive("heading", { level: 2 })}
          title="H2"
          disabled={viewSource}
        >
          H2
        </MenuButton>

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold"
          disabled={viewSource}
        >
          <b>B</b>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic"
          disabled={viewSource}
        >
          <i>I</i>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive("underline")}
          title="Underline"
          disabled={viewSource}
        >
          <u>U</u>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive("strike")}
          title="Strike"
          disabled={viewSource}
        >
          <s>S</s>
        </MenuButton>

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          isActive={editor.isActive({ textAlign: "left" })}
          title="Align Left"
          disabled={viewSource}
        >
          ←
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          isActive={editor.isActive({ textAlign: "center" })}
          title="Align Center"
          disabled={viewSource}
        >
          ↔
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          isActive={editor.isActive({ textAlign: "right" })}
          title="Align Right"
          disabled={viewSource}
        >
          →
        </MenuButton>

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet List"
          disabled={viewSource}
        >
          • List
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title="Ordered List"
          disabled={viewSource}
        >
          1. List
        </MenuButton>

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        <MenuButton
          onClick={() => openModal("link")}
          isActive={editor.isActive("link")}
          title="Insert Link"
          disabled={viewSource}
        >
          <Link2 />
        </MenuButton>
        {editor.isActive("link") && (
          <MenuButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            title="Remove Link"
            disabled={viewSource}
          >
            🚫
          </MenuButton>
        )}
        <MenuButton
          onClick={() => openModal("image")}
          title="Insert Image"
          disabled={viewSource}
        >
          <ImageDown />
        </MenuButton>
        <MenuButton
          onClick={() => openModal("video")}
          title="Insert Video"
          disabled={viewSource}
        >
          <Video />
        </MenuButton>

        <div className="w-px h-5 bg-neutral-300 mx-1" />

        {/* دکمه‌های داینامیک مدیریت جدول */}
        {!editor.isActive("table") ? (
          <MenuButton
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
            title="Insert Table"
            disabled={viewSource}
          >
            Table
          </MenuButton>
        ) : (
          <div className="flex items-center gap-0.5 bg-neutral-200/60 p-0.5 rounded-lg border border-neutral-300/50">
            <MenuButton
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              title="افزودن ستون قبل"
              disabled={viewSource}
            >
              +Column Left
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="افزودن ستون بعد"
              disabled={viewSource}
            >
              +Column Right
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="حذف ستون"
              disabled={viewSource}
            >
              -Col
            </MenuButton>
            <div className="w-px h-4 bg-neutral-300 mx-0.5" />
            <MenuButton
              onClick={() => editor.chain().focus().addRowBefore().run()}
              title="افزودن سطر قبل"
              disabled={viewSource}
            >
              +Row Above
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="افزودن سطر بعد"
              disabled={viewSource}
            >
              +Row Below
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="حذف سطر"
              disabled={viewSource}
            >
              -Row
            </MenuButton>
            <div className="w-px h-4 bg-neutral-300 mx-0.5" />
            <MenuButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="حذف کل جدول"
              disabled={viewSource}
            >
              🗑️ جدول
            </MenuButton>
          </div>
        )}

        {/* دکمه اختصاصی سوییچ منبع کد */}
        <div className="mr-auto">
          <button
            type="button"
            onClick={toggleSourceView}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all border ${
              viewSource
                ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
            }`}
          >
            {viewSource ? "Show Visual" : "View Source"}
          </button>
        </div>
      </div>

      {/* باکس محتوا */}
      <div className="relative bg-white">
        {viewSource ? (
          <textarea
            value={htmlContent}
            onChange={handleSourceChange}
            className="w-full min-h-[250px] p-4 font-mono text-sm bg-neutral-50 text-neutral-800 outline-none border-none resize-y block"
            dir="ltr"
          />
        ) : (
          <>
            <EditorContent editor={editor} />
            {editor.isEmpty && placeholder && (
              <div className="absolute top-4 right-4 text-neutral-400 pointer-events-none select-none text-sm">
                {placeholder}
              </div>
            )}
          </>
        )}
      </div>

      {/* مدال داینامیک و تکی بر اساس نوع نیاز */}
      {modalType && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          style={{ direction: "rtl" }}
        >
          <div className="bg-white border border-neutral-200 rounded-xl p-5 w-full max-w-md shadow-xl transition-all">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2 text-right">
              {modalType === "image" && "افزودن تصویر جدید"}
              {modalType === "video" && "افزودن ویدیو جدید"}
              {modalType === "link" && "افزودن لینک جدید"}
            </h3>

            <form onSubmit={handleModalSubmit} className="space-y-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1 text-right">
                  آدرس اینترنتی (URL):
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://example.com/..."
                  value={modalData.src}
                  onChange={(e) =>
                    setModalData({ ...modalData, src: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-lg border border-neutral-300 bg-neutral-50 text-neutral-900 text-left focus:outline-none focus:ring-2 focus:ring-neutral-900 text-sm"
                />
              </div>

              {/* فیلدهای اختصاصی عکس */}
              {modalType === "image" && (
                <>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1 text-right">
                      متن جایگزین (Alt):
                    </label>
                    <input
                      type="text"
                      placeholder="توضیح تصویر برای سئو"
                      value={modalData.alt}
                      onChange={(e) =>
                        setModalData({ ...modalData, alt: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-neutral-300 bg-neutral-50 text-neutral-900 text-right focus:outline-none focus:ring-2 focus:ring-neutral-900 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1 text-right">
                        (Width) عرض:
                      </label>
                      <input
                        type="text"
                        placeholder="مثلا 500px یا 100%"
                        value={modalData.width}
                        onChange={(e) =>
                          setModalData({ ...modalData, width: e.target.value })
                        }
                        className="w-full px-3 py-2 rounded-lg border border-neutral-300 bg-neutral-50 text-neutral-900 text-left focus:outline-none focus:ring-2 focus:ring-neutral-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1 text-right">
                        (Height) ارتفاع:
                      </label>
                      <input
                        type="text"
                        placeholder="مثلا 300px"
                        value={modalData.height}
                        onChange={(e) =>
                          setModalData({ ...modalData, height: e.target.value })
                        }
                        className="w-full px-3 py-2 rounded-lg border border-neutral-300 bg-neutral-50 text-neutral-900 text-left focus:outline-none focus:ring-2 focus:ring-neutral-900 text-sm"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalType(null)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100 transition"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-neutral-900 text-white hover:bg-neutral-800 transition shadow-sm"
                >
                  تایید و ثبت
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomEditor;
